"use client";

import { useEffect, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Stat = {
  value: number;
  suffix: string;
  prefix: string;
  label: string;
  description: string;
};
const stats: Stat[] = [{
  value: 25,
  suffix: "+",
  prefix: "16",
  label: "Years Experience",
  description: "Decades of precision converting expertise for industrial leaders."
}, {
  value: 99,
  suffix: "%",
  prefix: "",
  label: "Accuracy Rate",
  description: "Engineered tolerances for the most demanding aerospace specs."
}, {
  value: 500,
  suffix: "+",
  prefix: "",
  label: "Materials Processed",
  description: "Extensive library of films, adhesives, and specialty substrates."
}, {
  value: 24,
  suffix: "/7",
  prefix: "",
  label: "Support Access",
  description: "Direct technical support for North American manufacturing teams."
}];
function CountUp({
  end,
  isVisible,
  duration = 1800
}: {
  end: number;
  isVisible: boolean;
  duration?: number;
}) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    let startTime: number | null = null;
    let rafId: number;
    const tick = (timestamp: number) => {
      if (startTime === null) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(eased * end));
      if (progress < 1) {
        rafId = requestAnimationFrame(tick);
      } else {
        setCount(end);
      }
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [end, isVisible, duration]);
  return <>{count.toLocaleString()}</>;
}
export default function Advantage() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.3
  });
  return <section data-section-id="4518" id="the-gst-advantage" className="bg-[#F8F5F8] text-[#442872] py-24 md:py-40 px-6 md:px-12 relative overflow-hidden light">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-[0.03]">
        <svg viewBox="0 0 800 800" className="w-[140%] max-w-none" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" strokeWidth="0.5">
          <circle cx="400" cy="400" r="380" />
          <circle cx="400" cy="400" r="320" />
          <circle cx="400" cy="400" r="260" />
          <circle cx="400" cy="400" r="200" />
          <circle cx="400" cy="400" r="140" />
          <circle cx="400" cy="400" r="80" />
          <circle cx="250" cy="400" r="60" />
          <circle cx="550" cy="400" r="60" />
          <circle cx="400" cy="250" r="60" />
          <circle cx="400" cy="550" r="60" />
          <line x1="20" y1="400" x2="780" y2="400" />
          <line x1="400" y1="20" x2="400" y2="780" />
          <line x1="115" y1="115" x2="685" y2="685" />
          <line x1="685" y1="115" x2="115" y2="685" />
          {Array.from({
          length: 60
        }).map((_, i) => {
          const angle = i * 6 * Math.PI / 180;
          const x1 = 400 + Math.cos(angle) * 370;
          const y1 = 400 + Math.sin(angle) * 370;
          const x2 = 400 + Math.cos(angle) * 380;
          const y2 = 400 + Math.sin(angle) * 380;
          return <line key={i} data-index={i} x1={x1} y1={y1} x2={x2} y2={y2} />;
        })}
        </svg>
      </div>

      <div ref={ref} className="relative max-w-7xl mx-auto">
        {}
        <div className="mb-16 md:mb-24 max-w-3xl">
          <div className="flex items-center gap-3 mb-8">
            <span className="font-mono text-xs uppercase tracking-widest" style={{
            color: "#80729B"
          }}>
              Performance Metrics
            </span>
            <span className="font-mono text-xs tracking-widest text-[#80729B]">
              / 01
            </span>
          </div>
          <h2 className="font-default italic text-3xl md:text-5xl leading-tight text-[#442872]" style={{
          letterSpacing: "-0.02em"
        }}>
            Precision manufacturing, quantified for excellence.
          </h2>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-4 relative">
          {stats.map((stat, i) => <div data-index={i} key={i} className={["relative px-0 md:px-8 py-10 md:py-0", i !== 0 ? "border-t border-[#442872]/10 md:border-t-0" : "", i !== 0 ? "md:before:content-[''] md:before:absolute md:before:left-0 md:before:top-[10%] md:before:h-[80%] md:before:w-px md:before:bg-[#442872]/10" : ""].join(" ")}>
              <div className="flex flex-col">
                {}
                <div className="flex items-baseline flex-wrap">
                  <span className="font-default font-bold text-[#442872]" style={{
                fontSize: "clamp(5rem, 10vw, 9rem)",
                letterSpacing: "-0.06em",
                lineHeight: 0.9
              }}>
                    {stat.prefix}
                    <CountUp end={stat.value} isVisible={isVisible} />
                  </span>
                  {stat.suffix && <span className="font-mono text-xl md:text-2xl text-[#80729B] ml-1" style={{
                letterSpacing: "0"
              }}>
                      {stat.suffix}
                    </span>}
                </div>

                {}
                <div className="mt-4 mb-6 h-px w-full bg-[#442872]/10 overflow-hidden">
                  <div className="h-full" style={{
                backgroundColor: "#A492BC",
                width: isVisible ? "100%" : "0%",
                transition: "width 1800ms cubic-bezier(0.22, 1, 0.36, 1)"
              }} />
                </div>

                {}
                <div className="font-default text-xs uppercase tracking-widest text-[#442872] mb-3 font-medium" style={{
              letterSpacing: "0.15em"
            }}>
                  {stat.label}
                </div>

                {}
                <p className="font-default text-sm md:text-base text-[#80729B] leading-relaxed max-w-xs">
                  {stat.description}
                </p>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}